import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';

/**
 * Hook to check for newly-achieved milestones and queue celebrations.
 * 
 * Usage:
 *   const { celebrations, dismissCelebration, dismissAll, triggerCheck } = useMilestoneCheck();
 * 
 * - Auto-runs on mount with `autoCheck=true` (default)
 * - Returns celebrations array (empty if none/disabled)
 * - `triggerCheck()` to re-run manually after key actions (save net worth, etc.)
 */
export const useMilestoneCheck = (autoCheck = true) => {
  const [celebrations, setCelebrations] = useState([]);
  const [checking, setChecking] = useState(false);

  const triggerCheck = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    try {
      const response = await api.post('/milestones/check');
      if (response.data.celebrations && response.data.celebrations.length > 0) {
        setCelebrations(response.data.celebrations);
      }
      return response.data;
    } catch (error) {
      // Silent fail - don't disrupt user
      console.error('Milestone check failed:', error);
    } finally {
      setChecking(false);
    }
  }, [checking]);

  useEffect(() => {
    if (autoCheck) {
      triggerCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCheck]);

  const dismissAll = useCallback(() => setCelebrations([]), []);
  const dismissCelebration = useCallback((index) => {
    setCelebrations(prev => prev.filter((_, i) => i !== index));
  }, []);

  return {
    celebrations,
    checking,
    triggerCheck,
    dismissCelebration,
    dismissAll
  };
};
